/**
 * useSubscription - Hook para verificar limites do plano do usuário
 * Fornece acesso aos limites de funcionalidades baseado no plano ativo,
 * incluindo verificação de trial e expiração
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let cachedSubscription = null;
let cacheTimestamp = 0;
const CACHE_TTL = 60000; // 1 min

export function useSubscription() {
  const [subscription, setSubscription] = useState(cachedSubscription);
  const [loading, setLoading] = useState(!cachedSubscription);

  const fetchSubscription = useCallback(async () => {
    const now = Date.now();
    if (cachedSubscription && now - cacheTimestamp < CACHE_TTL) {
      setSubscription(cachedSubscription);
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/my-subscription`);
      cachedSubscription = res.data;
      cacheTimestamp = Date.now();
      setSubscription(res.data);
    } catch {
      // not logged in or error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const refresh = useCallback(() => {
    cachedSubscription = null;
    cacheTimestamp = 0;
    fetchSubscription();
  }, [fetchSubscription]);

  const limits = subscription?.limits || {};
  const planId = subscription?.plan_id || "";
  const planName = subscription?.plan_name || "";
  const isFree = subscription?.is_free !== false;
  const isTrial = subscription?.is_trial || false;
  const trialExpired = subscription?.trial_expired || false;
  const daysRemaining = subscription?.days_remaining ?? -1;
  const trialDaysTotal = subscription?.trial_days_total ?? 0;
  const alertLevel = subscription?.alert_level || null;
  const expiredPlanId = subscription?.expired_plan_id || null;

  /**
   * Verifica se o usuário pode acessar uma funcionalidade.
   * Se o trial expirou, TODAS as funcionalidades premium são bloqueadas
   * (o backend já retorna limites do plano gratuito quando trial expira).
   */
  const canAccess = useCallback((feature) => {
    if (!limits) return true;
    const val = limits[feature];
    if (val === undefined) return true;
    if (typeof val === "boolean") return val;
    if (val === -1) return true;
    return val > 0;
  }, [limits]);

  const getLimit = useCallback((feature) => {
    if (!limits) return -1;
    return limits[feature] ?? -1;
  }, [limits]);

  return {
    subscription,
    loading,
    limits,
    planId,
    planName,
    isFree,
    isTrial,
    trialExpired,
    daysRemaining,
    trialDaysTotal,
    alertLevel,
    expiredPlanId,
    canAccess,
    getLimit,
    refresh
  };
}
